// TourCard.jsx
import React, { useState } from "react"
import { motion } from "framer-motion"
import {
  Plane,
  Moon,
  MapPin,
  Waves,
  Utensils,
  ShieldCheck,
  Bus,
  Route,
  Calendar,
  Phone,
} from "lucide-react"
import { Trans, useTranslation } from "react-i18next"

import AmenityRow from "../AmenityRow"
import PhotoLinkButton from "../PhotoLinkButton"
import OffersList from "../OffersList"
import Lightbox from "../Lightbox"
import Thumbnails from "../Thumbnails"
import Stars from "../../../../../shared/components/Stars"
import { fmtDate, money } from "../../utils/parsers/common"
import Perforation from "../Perforation"
import { formatSavedAtTour } from "../../utils/parsers/date"

const TourCard = ({
  tour,
  index,
  showPhotos,
  editable = false,
  onChanged,
  selectable = false,
  selected = false,
  onToggleSelect,
  requestConsultation = false,
  onRequestConsultation,
  sessionKey,
}) => {
  const { t } = useTranslation()

  if (!tour) return null

  const roomPhotos = tour.room_photos || []
  const hotelPhotos = tour.hotel_details?.photos || []
  const [gallery, setGallery] = useState({ photos: [], index: null })
  const openGallery = (photos, i = 0) => setGallery({ photos, index: i })
  const closeGallery = () => setGallery((g) => ({ ...g, index: null }))
  const navGallery = (dir) =>
    setGallery((g) => ({ ...g, index: (g.index + dir + g.photos.length) % g.photos.length }))

  const hasFlightInfo =
    tour.terminal_name || tour.transfer_terminal_name || tour.transport_type_name

  const hasAirportInfo =
    tour.airport_depart_name ||
    tour.airport_arrival_name ||
    tour.airport_depart_code ||
    tour.airport_arrival_code

  const hasFlightDates = Boolean(tour.date_depart && tour.date_return)

  const formatAirport = (name, code) => {
    if (!name && !code) return "—"
    if (name && code) return `${name} (${code})`
    return name || code
  }

  return (
    <motion.article
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className={`flex flex-col md:flex-row bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow border overflow-hidden ${
        selectable && selected ? "border-[#E0554F] ring-1 ring-[#E0554F]/30" : "border-[#E7EAEF]"
      }`}
    >
      {selectable && (
        <label className="flex items-start pl-4 pt-4 md:pt-5 cursor-pointer">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggleSelect}
            className="w-4 h-4 mt-1 rounded border-[#C9D0DA] accent-[#E0554F] cursor-pointer"
          />
        </label>
      )}

      <div className="flex-1 p-5 flex flex-col md:flex-row gap-4">
        {showPhotos && (
          <Thumbnails photos={roomPhotos} onOpen={(i) => openGallery(roomPhotos, i)} />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Stars label={tour.star_name} />
                <span className="text-[11px] uppercase tracking-wide text-[#9AA5B4] font-mono">
                  {tour.star_name}
                </span>
                {tour.on_request && (
                  <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-[#FFF4E5] text-[#E8871E]">
                    {t("saved_tours.on_request")}
                  </span>
                )}
                {tour.guaranteed && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-[#E9F7EF] text-[#1E9E5B]">
                    <ShieldCheck size={11} /> {t("saved_tours.guaranteed")}
                  </span>
                )}
              </div>
              <h3
                className="text-lg md:text-xl text-[#26303D] leading-tight"
                style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
              >
                {tour.hotel_name}
              </h3>
              <p className="flex items-center gap-1 text-sm text-[#7C8697] mt-0.5">
                <MapPin size={13} />
                {tour.resort_name}, {tour.region_name}
              </p>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[11px] text-[#9AA5B4]">{t("saved_tours.from")}</div>
              <div className="text-base font-mono font-semibold text-[#26303D]">
                {money(tour.minPrice, tour.currency)}
              </div>
            </div>
          </div>

          {tour.room_description && (
            <p className="text-sm text-[#7C8697] mt-3 leading-relaxed line-clamp-2">
              {tour.room_description}
            </p>
          )}
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {tour.type_room_name && (
              <p className="text-xs text-[#9AA5B4] font-mono">{tour.type_room_name}</p>
            )}
            {tour.food_name && (
              <span className="inline-flex items-center gap-1 text-xs text-[#5A6779] font-mono">
                <Utensils size={12} /> {tour.food_name}
              </span>
            )}
            {tour.package_info && (
              <span className="text-xs text-[#5A6779] font-mono">{tour.package_info}</span>
            )}
          </div>

          {tour.hotel_details?.services?.length > 0 && (
            <div className="mt-3">
              <AmenityRow services={tour.hotel_details.services} />
            </div>
          )}

          <div className="mt-3 flex items-center gap-4">
            <PhotoLinkButton
              label={t("saved_tours.room_photos")}
              count={roomPhotos.length}
              onClick={() => openGallery(roomPhotos, 0)}
            />
            <PhotoLinkButton
              label={t("saved_tours.hotel_photos")}
              count={hotelPhotos.length}
              onClick={() => openGallery(hotelPhotos, 0)}
            />
          </div>

          <div className="mt-4 flex items-center gap-4 text-xs text-[#5A6779] border-t border-dashed border-[#E7EAEF] pt-3 flex-wrap">
            {tour.has_transport ? (
              <span className="inline-flex items-center gap-1.5">
                <Plane size={13} />
                {hasAirportInfo
                  ? `${formatAirport(tour.airport_depart_name, tour.airport_depart_code)} → ${formatAirport(
                      tour.airport_arrival_name,
                      tour.airport_arrival_code
                    )}`
                  : hasFlightInfo
                    ? `${tour.transport_type_name ? tour.transport_type_name + " · " : ""}${
                        tour.terminal_name || "—"
                      } → ${tour.transfer_terminal_name || "—"}`
                    : t("saved_tours.flight_included")}
                {tour.class_seat_name && ` · ${tour.class_seat_name}`}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[#9AA5B4]">
                <Plane size={13} className="opacity-40" /> {t("saved_tours.no_flight")}
              </span>
            )}

            {typeof tour.stop_count === "number" && (
              <span className="inline-flex items-center gap-1.5">
                <Route size={13} />{" "}
                {tour.stop_count === 0
                  ? t("saved_tours.direct")
                  : t("saved_tours.stops", {
                      count: tour.stop_count,
                    })}
              </span>
            )}

            {tour.has_transfer && (
              <span className="inline-flex items-center gap-1.5">
                <Bus size={13} /> {t("saved_tours.transfer_included")}
              </span>
            )}
          </div>

          {tour.has_transport && hasFlightDates && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-[#9AA5B4]">
              <Plane size={12} className="opacity-60" />
              {t("saved_tours.flight")}: {fmtDate(tour.date_depart)}
              {tour.time_depart ? ` ${tour.time_depart}` : ""} → {fmtDate(tour.date_return)}
              {tour.time_return ? ` ${tour.time_return}` : ""}
            </div>
          )}

          {tour.saved_from?.source_name && (
            <div className="text-[10px] text-[#9AA5B4] mt-2">
              {t("saved_tours.source")}: {tour.saved_from.source_name}
              {formatSavedAtTour(tour.saved_from.saved_at) &&
                ` · ${t("saved_tours.saved")} ${formatSavedAtTour(tour.saved_from.saved_at)}`}
            </div>
          )}
        </div>
      </div>

      <Perforation />

      <div className="w-full md:w-72 shrink-0 p-5 flex flex-col justify-between gap-4 bg-[#F4F6F9] md:rounded-r-2xl rounded-b-2xl md:rounded-bl-none">
        <div>
          <div className="flex items-center justify-between text-xs font-mono text-[#5A6779] mb-1">
            <span className="flex items-center gap-1">
              <Calendar size={12} /> {fmtDate(tour.date_entry)}
            </span>
            <span className="text-[#9AA5B4]">→</span>
            <span className="flex items-center gap-1">
              {fmtDate(tour.date_out)} <Calendar size={12} />
            </span>
          </div>
          <div className="flex items-center gap-1 text-xs text-[#5A6779] mb-3">
            <Moon size={12} />{" "}
            {t("saved_tours.nights", {
              count: tour.nights,
            })}
          </div>
        </div>

        <div>
          <OffersList
            tour={tour}
            offers={tour.offers}
            currency={tour.currency}
            editable={editable}
            onChanged={onChanged}
            sessionKey={sessionKey}
          />

          {requestConsultation && (
            <button
              onClick={() => onRequestConsultation?.(tour)}
              className="mt-3 w-full flex items-center justify-center gap-1.5 text-sm text-white bg-[#26303D] rounded-lg px-4 py-2.5 hover:opacity-90 transition-opacity"
            >
              <Phone size={14} /> {t("saved_tours.request_consultation")}
            </button>
          )}
        </div>
      </div>

      <Lightbox
        photos={gallery.photos}
        index={gallery.index}
        onClose={closeGallery}
        onNav={navGallery}
      />
    </motion.article>
  )
}

export default TourCard
